import {
  AllowedDataLineageFilterOption,
  DataLineageFilterType,
  DataLineageLink,
  DataLineageLinkType,
  DataLineageNodeTypes,
  DataLineageSelectedFilter,
  JobStatus,
  Y42AssetStatus,
} from '@y42/json-schema';
import groupBy from 'lodash/groupBy';
import {
  DataLineageDiagramUIModel,
  DataLineageNodeBinding,
  DataLineageUINode,
} from '../../../../state/data-lineage-diagram';

interface DataLineageGroup {
  value: DataLineageUINode;
  children: DataLineageUINode[];
}

type DataLineageGroupMap = Record<string, DataLineageGroup>;

export const filterDataLineageModel = (
  model: DataLineageDiagramUIModel,
  filters: DataLineageSelectedFilter[],
): DataLineageDiagramUIModel => {
  if (filters.length < 1) {
    return model;
  }

  const { linkDataArray, visualDataArray } = model;

  const filteredGroupNodes = filterGroupedNodes(model, filters);
  const filteredWidgetNodes = filterWidgetNodes(model, filters);

  const filteredNodes = [...filteredGroupNodes, ...filteredWidgetNodes];

  const filteredNodeKeys = filteredNodes.map((n) => n.key);
  const filteredLinks = linkDataArray.filter(
    (l) => filteredNodeKeys.includes(l.from) || filteredNodeKeys.includes(l.to),
  );

  return { linkDataArray: filteredLinks, nodeDataArray: filteredNodes, visualDataArray };
};

const filterGroupedNodes = (
  model: DataLineageDiagramUIModel,
  filters: DataLineageSelectedFilter[],
): DataLineageUINode[] => {
  const { nodeDataArray } = model;

  const groupedByGroupId = groupBy(nodeDataArray, (n: DataLineageUINode) => n.group);

  const dataLineageGroupMap: DataLineageGroupMap = {};
  nodeDataArray
    .filter((n) => n.isGroup)
    .forEach((n) => {
      dataLineageGroupMap[n.key] = {
        value: n,
        children: (groupedByGroupId[n.key] || []).filter((node) => node.category !== DataLineageNodeTypes.Empty),
      };
    });

  return Object.values(dataLineageGroupMap)
    .filter((group) => group.children.length > 0)
    .map((group) => getFilteredGroupesAndNodes(filters, group, model))
    .flat();
};

const filterWidgetNodes = (
  model: DataLineageDiagramUIModel,
  filters: DataLineageSelectedFilter[],
): DataLineageUINode[] => {
  const { nodeDataArray } = model;
  return nodeDataArray
    .filter((n) => n.category === DataLineageNodeTypes.Widget)
    .filter((n) => checkAllFilters(n, filters, model));
};

const getFilteredGroupesAndNodes = (
  filters: DataLineageSelectedFilter[],
  group: DataLineageGroup,
  model: DataLineageDiagramUIModel,
): DataLineageUINode[] => {
  if (hasGroupFilters(filters) && checkGroupFilters(group.value, filters)) {
    const filteredNodes = group.children.filter((n) => checkOnlyNodeFilters(n, filters, model));
    return filteredNodes.length > 0 ? [group.value, ...filteredNodes] : [group.value, ...group.children];
  }

  const filteredNodes = group.children.filter((n) => checkAllFilters(n, filters, model));

  if (filteredNodes.length > 0) {
    filteredNodes.push(group.value);
  }

  return filteredNodes;
};

const hasGroupFilters = (filters: DataLineageSelectedFilter[]): boolean => {
  return (
    filters.filter((f) =>
      [DataLineageFilterType.Module, DataLineageFilterType.AssetStatus, DataLineageFilterType.Tag].includes(f.type),
    ).length > 0
  );
};

const checkGroupFilters = (groupNode: DataLineageUINode, filters: DataLineageSelectedFilter[]): boolean => {
  const conditions: boolean[] = [];

  conditions.push(moduleFilter(groupNode, filters));
  conditions.push(assetStatusFilter(groupNode, filters));
  conditions.push(tagFilter(groupNode, filters));

  return conditions.every((c) => !!c);
};

const checkAllFilters = (
  node: DataLineageUINode,
  filters: DataLineageSelectedFilter[],
  model: DataLineageDiagramUIModel,
): boolean => {
  const conditions: boolean[] = [];

  conditions.push(connectionFilter(node, filters, model));
  conditions.push(lastJobStatusFilter(node, filters));
  conditions.push(moduleFilter(node, filters));
  conditions.push(assetStatusFilter(node, filters));
  conditions.push(tagFilter(node, filters));

  return conditions.every((c) => !!c);
};

const checkOnlyNodeFilters = (
  node: DataLineageUINode,
  filters: DataLineageSelectedFilter[],
  model: DataLineageDiagramUIModel,
): boolean => {
  const conditions: boolean[] = [];

  conditions.push(connectionFilter(node, filters, model));
  conditions.push(lastJobStatusFilter(node, filters));
  conditions.push(tagFilter(node, filters));

  return conditions.every((c) => !!c);
};

const getSelectedFilterValues = (
  filters: DataLineageSelectedFilter[],
  filterType: DataLineageFilterType,
): AllowedDataLineageFilterOption[] => {
  return filters.filter((f) => f.type === filterType).map((f) => f.value);
};

const connectionFilter = (
  node: DataLineageUINode,
  filters: DataLineageSelectedFilter[],
  model: DataLineageDiagramUIModel,
): boolean => {
  const connectionValues = getSelectedFilterValues(filters, DataLineageFilterType.Link);

  if (connectionValues.length === 0) {
    return true;
  }

  if (
    connectionValues.includes(DataLineageLinkType.Connected) &&
    connectionValues.includes(DataLineageLinkType.Unconnected)
  ) {
    return true;
  }

  const hasConnectedNodes = (nodeData: DataLineageUINode, links: DataLineageLink[]) => {
    return !!links.filter((l) => l.from === nodeData.key || l.to === nodeData.key).length;
  };

  const { linkDataArray } = model;
  const isConnectedToNodes = hasConnectedNodes(node, linkDataArray);

  return connectionValues.includes(DataLineageLinkType.Connected) ? isConnectedToNodes : !isConnectedToNodes;
};

const moduleFilter = (node: DataLineageUINode, filters: DataLineageSelectedFilter[]): boolean => {
  const moduleFilterValues = getSelectedFilterValues(filters, DataLineageFilterType.Module);

  if (moduleFilterValues.length === 0) {
    return true;
  }

  if (node.category === DataLineageNodeTypes.Empty) {
    return false;
  }

  return moduleFilterValues.includes(node.category as DataLineageNodeTypes);
};

const lastJobStatusFilter = (node: DataLineageUINode, filters: DataLineageSelectedFilter[]): boolean => {
  const jobStatusFilterValues = getSelectedFilterValues(filters, DataLineageFilterType.LastJobStatus);

  if (jobStatusFilterValues.length === 0) {
    return true;
  }

  if (!node[DataLineageNodeBinding.Status]) {
    return false;
  }

  return jobStatusFilterValues.includes(node[DataLineageNodeBinding.Status] as JobStatus);
};

const assetStatusFilter = (node: DataLineageUINode, filters: DataLineageSelectedFilter[]): boolean => {
  const assetStatusFilterValues = getSelectedFilterValues(filters, DataLineageFilterType.AssetStatus);

  if (assetStatusFilterValues.length === 0) {
    return true;
  }

  if (!node[DataLineageNodeBinding.AssetStatus]) {
    return false;
  }

  return assetStatusFilterValues.includes(node[DataLineageNodeBinding.AssetStatus] as Y42AssetStatus);
};

const tagFilter = (node: DataLineageUINode, filters: DataLineageSelectedFilter[]): boolean => {
  const tagFilterValues = getSelectedFilterValues(filters, DataLineageFilterType.Tag);

  if (tagFilterValues.length === 0) {
    return true;
  }

  return tagFilterValues.some((tagValue) => node.tags.includes(tagValue));
};
